import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Settings,
  Play,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Trash2,
  Save,
  X,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { eligibilityApi } from '../../services/api';

// Comparison type options
const COMPARISON_TYPES = [
  { value: 'exact', label: 'Exact Match' },
  { value: 'case_insensitive', label: 'Case Insensitive' },
  { value: 'date', label: 'Date Comparison' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts With' },
];

// Column Mapping Modal
function MappingModal({ isOpen, onClose, onSave, existingMappings, saving = false }) {
  const [mappingName, setMappingName] = useState('');
  const [mappings, setMappings] = useState([]);
  const [isNewConfig, setIsNewConfig] = useState(true);

  // Fetch source and target columns
  const { data: sourceColumnsData, isLoading: loadingSource } = useQuery({
    queryKey: ['sourceColumns'],
    queryFn: () => eligibilityApi.getSourceColumns(),
    enabled: isOpen,
  });

  const { data: targetColumnsData, isLoading: loadingTarget } = useQuery({
    queryKey: ['targetColumns'],
    queryFn: () => eligibilityApi.getTargetColumns(),
    enabled: isOpen,
  });

  const sourceColumns = sourceColumnsData?.data || [];
  const targetColumns = targetColumnsData?.data || [];

  // Load existing mappings when editing
  useEffect(() => {
    if (existingMappings && existingMappings.length > 0) {
      setMappingName(existingMappings[0].mapping_name);
      setMappings(existingMappings.map(m => ({
        source_column: m.source_column,
        target_column: m.target_column,
        comparison_type: m.comparison_type,
        is_key_field: m.is_key_field,
      })));
      setIsNewConfig(false);
    } else {
      setMappingName('');
      setMappings([{
        source_column: '',
        target_column: '',
        comparison_type: 'exact',
        is_key_field: true,
      }]);
      setIsNewConfig(true);
    }
  }, [existingMappings, isOpen]);

  const addMapping = () => {
    setMappings([...mappings, {
      source_column: '',
      target_column: '',
      comparison_type: 'exact',
      is_key_field: false,
    }]);
  };

  const removeMapping = (index) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const updateMapping = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  const handleSave = () => {
    if (!mappingName.trim()) {
      alert('Please enter a mapping configuration name');
      return;
    }
    if (mappings.length === 0) {
      alert('Please add at least one column mapping');
      return;
    }
    // Check all mappings have both source and target columns
    const incompleteMappings = mappings.filter(m => !m.source_column || !m.target_column);
    if (incompleteMappings.length > 0) {
      alert('Please select both source and target columns for all mappings');
      return;
    }
    if (!mappings.some(m => m.is_key_field)) {
      alert('Please mark at least one mapping as a key field');
      return;
    }
    console.log('Saving mappings:', { mappingName, mappings });
    onSave(mappingName, mappings);
  };

  if (!isOpen) return null;

  const isLoading = loadingSource || loadingTarget;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            {isNewConfig ? 'Create Column Mapping' : 'Edit Column Mapping'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Mapping Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Configuration Name
                </label>
                <input
                  type="text"
                  value={mappingName}
                  onChange={(e) => setMappingName(e.target.value)}
                  placeholder="e.g., Default Eligibility Mapping"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Tables Info */}
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Source Table</p>
                  <p className="font-mono text-xs font-medium text-gray-900">etl_staging.etl_outbound.member_import_file_data</p>
                  <p className="text-xs text-gray-400">{sourceColumns.length} columns</p>
                </div>
                <ArrowRight className="w-6 h-6 text-gray-400" />
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Target Table</p>
                  <p className="font-mono text-sm font-medium text-gray-900">huser.consumer</p>
                  <p className="text-xs text-gray-400">{targetColumns.length} columns</p>
                </div>
              </div>

              {/* Column Mappings */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Column Mappings
                  </label>
                  <button
                    onClick={addMapping}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                    Add Mapping
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase px-2">
                    <div className="col-span-3">Source Column</div>
                    <div className="col-span-3">Target Column</div>
                    <div className="col-span-3">Comparison</div>
                    <div className="col-span-2 text-center">Key Field</div>
                    <div className="col-span-1"></div>
                  </div>

                  {mappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded-lg">
                      <div className="col-span-3">
                        <select
                          value={mapping.source_column}
                          onChange={(e) => updateMapping(index, 'source_column', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select column...</option>
                          {sourceColumns.map((col) => (
                            <option key={col.column_name} value={col.column_name}>
                              {col.column_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={mapping.target_column}
                          onChange={(e) => updateMapping(index, 'target_column', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select column...</option>
                          {targetColumns.map((col) => (
                            <option key={col.column_name} value={col.column_name}>
                              {col.column_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={mapping.comparison_type}
                          onChange={(e) => updateMapping(index, 'comparison_type', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          {COMPARISON_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <input
                          type="checkbox"
                          checked={mapping.is_key_field}
                          onChange={(e) => updateMapping(index, 'is_key_field', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          onClick={() => removeMapping(index)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          disabled={mappings.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? 'Saving...' : 'Save Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const configs = {
    match: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    mismatch: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    one_null: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle },
    both_null: { bg: 'bg-gray-100', text: 'text-gray-500', icon: null },
    found: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    not_found: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  };

  const config = configs[status] || configs.mismatch;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {status.replace('_', ' ')}
    </span>
  );
}

// Main Component
export default function EligibilityCheck() {
  const [selectedConfig, setSelectedConfig] = useState('Default Eligibility Mapping');
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editingMappings, setEditingMappings] = useState(null);
  const [comparisonLimit, setComparisonLimit] = useState(100);
  const [mismatchesOnly, setMismatchesOnly] = useState(true);

  const queryClient = useQueryClient();

  // Fetch mapping configurations
  const { data: configsData, isLoading: loadingConfigs } = useQuery({
    queryKey: ['mappingConfigs'],
    queryFn: () => eligibilityApi.getMappingConfigs(),
  });

  const configs = configsData?.data || [];

  // Fetch current mappings
  const { data: mappingsData, isLoading: loadingMappings } = useQuery({
    queryKey: ['columnMappings', selectedConfig],
    queryFn: () => eligibilityApi.getColumnMappings(selectedConfig),
    enabled: !!selectedConfig,
  });

  const currentMappings = mappingsData?.data || [];

  // Save mappings mutation
  const saveMappingsMutation = useMutation({
    mutationFn: ({ mappingName, mappings }) =>
      eligibilityApi.saveMappings(mappingName, mappings),
    onSuccess: () => {
      console.log('Save successful, closing modal');
      queryClient.invalidateQueries({ queryKey: ['mappingConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['columnMappings'] });
      setShowMappingModal(false);
      setEditingMappings(null);
    },
    onError: (error) => {
      console.error('Save failed:', error);
      alert(`Failed to save mappings: ${error.message}`);
    },
  });

  // Run comparison mutation
  const compareMutation = useMutation({
    mutationFn: ({ mappingName, limit, mismatchesOnly }) =>
      eligibilityApi.compare(mappingName, limit, mismatchesOnly),
  });

  const handleRunComparison = () => {
    compareMutation.mutate({
      mappingName: selectedConfig,
      limit: comparisonLimit,
      mismatchesOnly,
    });
  };

  const handleEditMapping = () => {
    setEditingMappings(currentMappings);
    setShowMappingModal(true);
  };

  const handleNewMapping = () => {
    setEditingMappings(null);
    setShowMappingModal(true);
  };

  const handleSaveMapping = (mappingName, mappings) => {
    saveMappingsMutation.mutate({ mappingName, mappings });
  };

  const comparisonResult = compareMutation.data?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-600" />
            Eligibility Check
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Compare member import data against consumer records
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleNewMapping}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            New Mapping
          </button>
          <button
            onClick={handleEditMapping}
            disabled={!selectedConfig || loadingMappings}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <Settings className="w-4 h-4" />
            Edit Mapping
          </button>
        </div>
      </div>

      {/* Configuration Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mapping Configuration
            </label>
            <select
              value={selectedConfig}
              onChange={(e) => setSelectedConfig(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loadingConfigs}
            >
              {configs.map((config) => (
                <option key={config} value={config}>
                  {config}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Record Limit
            </label>
            <input
              type="number"
              value={comparisonLimit}
              onChange={(e) => setComparisonLimit(parseInt(e.target.value) || 100)}
              min={10}
              max={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter
            </label>
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={mismatchesOnly}
                onChange={(e) => setMismatchesOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Mismatches Only</span>
            </label>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleRunComparison}
              disabled={compareMutation.isPending || !selectedConfig}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {compareMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run Comparison
            </button>
          </div>
        </div>

        {/* Current Mappings Display */}
        {currentMappings.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Active Mappings:</p>
            <div className="flex flex-wrap gap-2">
              {currentMappings.map((m, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    m.is_key_field ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {m.source_column}
                  <ArrowRight className="w-3 h-3" />
                  {m.target_column}
                  {m.is_key_field && <span className="text-blue-500">(key)</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Comparison Error */}
      {compareMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Comparison Failed</h4>
            <p className="text-sm text-red-700 mt-1">
              {compareMutation.error?.response?.data?.message || compareMutation.error?.message}
            </p>
          </div>
        </div>
      )}

      {/* Comparison Results */}
      {comparisonResult && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">{comparisonResult.summary.total}</p>
            </div>
            <div className="bg-green-50 rounded-lg border border-green-200 p-4">
              <p className="text-sm text-green-600">Found in Consumer</p>
              <p className="text-2xl font-bold text-green-700">
                {comparisonResult.summary.found_in_consumer}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-4">
              <p className="text-sm text-red-600">Not Found</p>
              <p className="text-2xl font-bold text-red-700">
                {comparisonResult.summary.not_found}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
              <p className="text-sm text-yellow-600">Field Mismatches</p>
              <p className="text-2xl font-bold text-yellow-700">
                {Object.values(comparisonResult.summary.field_mismatches).reduce((a, b) => a + b, 0)}
              </p>
            </div>
          </div>

          {/* Field Mismatch Details */}
          {Object.keys(comparisonResult.summary.field_mismatches).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Mismatches by Field</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(comparisonResult.summary.field_mismatches).map(([field, count]) => (
                  <div
                    key={field}
                    className={`px-3 py-1.5 rounded-lg ${
                      count > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}
                  >
                    <span className="font-medium">{field}:</span> {count}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">
                Comparison Results ({comparisonResult.records.length} records)
                {mismatchesOnly && <span className="text-gray-400 ml-2">- Mismatches Only</span>}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Member ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Create Time
                    </th>
                    {comparisonResult.mappings.filter(m => !m.is_key_field).map((m) => (
                      <th
                        key={m.source_column}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        <div>{m.source_column}</div>
                        <div className="text-[10px] text-gray-400 font-normal normal-case">
                          Source / Target
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonResult.records.slice(0, 50).map((record, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-mono text-gray-900">
                        {record.member_id}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {record.create_ts ? new Date(record.create_ts).toLocaleDateString() : '-'}
                      </td>
                      {comparisonResult.mappings.filter(m => !m.is_key_field).map((m) => {
                        const status = record[`${m.source_column}_status`];
                        const isMismatch = status === 'mismatch';
                        // Use formatted date values if available (for date comparisons)
                        const sourceVal = record[`${m.source_column}_formatted`] || record[m.source_column];
                        const targetVal = record[`target_${m.target_column}_formatted`] || record[`target_${m.target_column}`];
                        // Debug: raw values and lengths for exact comparisons
                        const sourceRaw = record[`${m.source_column}_raw`];
                        const targetRaw = record[`target_${m.target_column}_raw`];
                        const sourceLen = record[`${m.source_column}_len`];
                        const targetLen = record[`target_${m.target_column}_len`];
                        const hasDebugInfo = sourceRaw !== undefined || targetRaw !== undefined;

                        return (
                          <td key={m.source_column} className="px-4 py-2">
                            <div className="space-y-1">
                              <StatusBadge status={status} />
                              {isMismatch && (
                                <div className="text-xs space-y-0.5">
                                  <div className="text-blue-600 truncate max-w-[200px]" title={sourceRaw || sourceVal}>
                                    S: {sourceVal || '(null)'} {sourceLen !== undefined && <span className="text-gray-400">({sourceLen} chars)</span>}
                                  </div>
                                  <div className="text-orange-600 truncate max-w-[200px]" title={targetRaw || targetVal}>
                                    T: {targetVal || '(null)'} {targetLen !== undefined && <span className="text-gray-400">({targetLen} chars)</span>}
                                  </div>
                                  {hasDebugInfo && sourceLen !== targetLen && (
                                    <div className="text-red-500 text-[10px]">
                                      Length differs: {sourceLen} vs {targetLen}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {comparisonResult.records.length > 50 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500 text-center">
                Showing first 50 of {comparisonResult.records.length} records
              </div>
            )}
          </div>
        </>
      )}

      {/* Mapping Modal */}
      <MappingModal
        isOpen={showMappingModal}
        onClose={() => {
          setShowMappingModal(false);
          setEditingMappings(null);
        }}
        onSave={handleSaveMapping}
        existingMappings={editingMappings}
        saving={saveMappingsMutation.isPending}
      />
    </div>
  );
}
